<script setup lang="ts">
import {onMounted, onBeforeUnmount, watch} from 'vue';
import {useTour, type TourStep} from '@/ts/composables/stateful/use-tour';
import {TOUR_CONFIG, TOUR_NAMES} from '@/ts/utils/tour-constants';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {useScreenSize} from '@/ts/composables/stateful/use-screen-size';
import {useRoute} from 'vue-router';
import {LANGUAGE_REGEX} from '@/ts/utils/consts';
import {useTourStore} from '@/ts/store/tour-store';
import {storeToRefs} from 'pinia';

const TOUR_NAME = TOUR_NAMES.onboardingTour;

const props = defineProps<{
  enabled?: boolean;
}>();

const tourConfig = TOUR_CONFIG[TOUR_NAME];
const {canAccessExplore, canAccessSidebar} = useFeatureAccess();
const {isMobileResolution} = useScreenSize();
const route = useRoute();
const tourStore = useTourStore();
const {pendingTourTrigger} = storeToRefs(tourStore);

let clickInterceptorCleanup: (() => void) | null = null;

const {startTour, startTourForced, resetTour, hasSeenTour, cleanupDriverDOM, getCurrentDriver} = useTour({
  enabled: props.enabled,
  tourName: TOUR_NAME,
});

// Define tour steps
const getTourSteps = (): TourStep[] => {
  const steps: TourStep[] = [];
  const isMobile = isMobileResolution.value;

  // On mobile, Explore/Paths/Sidebar highlight the hamburger menu
  const hamburgerSelector = '[data-tour="hamburger-menu"]';

  // Today (IPD-2981). Anchored to the nav tab rather than to anything inside the bento: the copy is
  // about the page as a whole, and a tab is the one target that is in the same place on every one of
  // its states. The step the Today page replaced pointed at a block that no longer exists and was
  // removed in the redesign rather than repointed, which left the tour with no opening step at all.
  steps.push({
    element: isMobile ? hamburgerSelector : '[data-tour="home"]',
    titleKey: 'onboardingTour.today.title',
    descriptionKey: 'onboardingTour.today.description',
    side: 'bottom',
    align: 'start',
  });

  // Explore - only if user has access to explore
  if (canAccessExplore.value) {
    steps.push({
      element: isMobile ? hamburgerSelector : '[data-tour="recco_explore"]',
      titleKey: 'onboardingTour.explore.title',
      descriptionKey: 'onboardingTour.explore.description',
      side: 'bottom',
      align: 'start',
    });
  }

  // Paths
  steps.push({
    element: isMobile ? hamburgerSelector : '[data-tour="paths"]',
    titleKey: 'onboardingTour.paths.title',
    descriptionKey: 'onboardingTour.paths.description',
    side: 'bottom',
    align: 'start',
  });

  // Journal
  steps.push({
    element: isMobile ? hamburgerSelector : '[data-tour="journal"]',
    titleKey: 'onboardingTour.journal.title',
    descriptionKey: 'onboardingTour.journal.description',
    side: 'bottom',
    align: 'start',
  });

  // Desktop: Account then Sidebar
  // Mobile: Sidebar then Account (order 1,2,4,3)
  if (isMobile) {
    // Sidebar - only if user has access
    if (canAccessSidebar.value) {
      steps.push({
        element: hamburgerSelector,
        titleKey: 'onboardingTour.sidebar.title',
        descriptionKey: 'onboardingTour.sidebar.description',
        side: 'bottom',
        align: 'start',
      });
    }

    // Account
    steps.push({
      element: '[data-tour="account"]',
      titleKey: 'onboardingTour.account.title',
      descriptionKey: 'onboardingTour.account.description',
      side: 'bottom',
      align: 'start',
    });
  } else {
    // Account
    steps.push({
      element: '[data-tour="account"]',
      titleKey: 'onboardingTour.account.title',
      descriptionKey: 'onboardingTour.account.description',
      side: 'bottom',
      align: 'start',
    });

    // Sidebar - only if user has access
    if (canAccessSidebar.value) {
      steps.push({
        element: '.js-sidebar-toolbar',
        titleKey: 'onboardingTour.sidebar.title',
        descriptionKey: 'onboardingTour.sidebar.description',
        side: 'left',
        align: 'start',
      });
    }
  }

  return steps;
};

// Cleanup tour
function cleanupTour(): void {
  if (clickInterceptorCleanup) {
    clickInterceptorCleanup();
    clickInterceptorCleanup = null;
  }
  cleanupDriverDOM();
}

// Prevent clicks outside the popover buttons
function preventBackgroundClicks(e: Event): void {
  const target = e.target as HTMLElement;

  if (target.closest('.driver-popover-next-btn, .driver-popover-prev-btn, .driver-popover-close-btn')) {
    return;
  }

  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
}

// Setup click prevention
function setupClickPrevention(): void {
  document.addEventListener('click', preventBackgroundClicks, true);
  clickInterceptorCleanup = (): void => {
    document.removeEventListener('click', preventBackgroundClicks, true);
  };
}

// Check if current route matches any of the tour's trigger paths
function isOnTriggerPath(): boolean {
  const pathWithoutLang = route.path.replace(LANGUAGE_REGEX.PATH_WITH_LANG, '/');
  return tourConfig.triggerPaths.some(triggerPath =>
    pathWithoutLang === triggerPath || pathWithoutLang === `${triggerPath}/`);
}

// The pending open, so a second arrival cannot leave a first one running behind it.
let openTimer: ReturnType<typeof setTimeout> | null = null;

function cancelPendingOpen(): void {
  if (openTimer) {
    clearTimeout(openTimer);
    openTimer = null;
  }
}

/**
 * Opens the tour on the one path it belongs to.
 *
 * The re-check on every route change is the point: this component sits on the parent route of the
 * whole authenticated app and never remounts, so the single check it used to make at mount ran
 * wherever the reader happened to land. Anyone entering the app somewhere other than home — a deep
 * link, a redirect — lost the tour for the entire session, and nothing was recorded as seen, so it
 * turned up on their NEXT login instead: the one login it is not for.
 *
 * The delay is why the conditions are checked twice. A second passes between deciding to open and
 * opening, and a reader can cross home in less than that: the targets the tour points at are the
 * navigation, which is on every authenticated page, so an open that fired late would land on
 * whatever page they had moved on to. Any pending open is cancelled before another is scheduled,
 * and where they are is read again when it comes due.
 *
 * A tour already on screen is left alone. Arriving at home is reachable while one is up — the
 * click interception does not cover browser back and forward, and the sidebar can force one open —
 * and `startTour` would destroy the live driver and drive the same tour again from step one. Worse
 * than the restart: the driver it destroyed calls `cleanupDriverDOM` 300ms later, which strips
 * every popover and overlay in the document, the replacement's included.
 */
function startProductTour(): void {
  if (getCurrentDriver()?.isActive() === true) {
    return;
  }

  cancelPendingOpen();

  if (!props.enabled || hasSeenTour() || !isOnTriggerPath()) {
    return;
  }

  if (getTourSteps().length === 0) {
    return;
  }

  openTimer = setTimeout(() => {
    openTimer = null;
    if (hasSeenTour() || !isOnTriggerPath() || getCurrentDriver()?.isActive() === true) {
      return;
    }

    const driverInstance = startTour(getTourSteps(), {
      onDestroyed: cleanupTour,
    });

    if (driverInstance) {
      setupClickPrevention();
    }
  }, 1000);
}

// Watch for external tour trigger (e.g., from sidebar icon)
watch(pendingTourTrigger, (tourName) => {
  if (tourName === TOUR_NAME) {
    tourStore.consumeTourTrigger();
    const steps = getTourSteps();
    if (steps.length > 0) {
      startTourForced(steps, {
        onDestroyed: cleanupTour,
      });
      setupClickPrevention();
    }
  }
});

// The tour's own path is the only one it opens on, and this component outlives every navigation
// between them, so a reader who enters the app elsewhere gets their look when they reach it.
watch(() => route.path, () => {
  startProductTour();
});

onMounted(() => {
  startProductTour();
});

onBeforeUnmount(() => {
  cancelPendingOpen();
  cleanupTour();
});

// Expose method to restart tour
defineExpose({
  restartTour: async() => {
    await resetTour();
    setTimeout(() => {
      const steps = getTourSteps();
      startTour(steps, {
        onDestroyed: cleanupTour,
      });
      setupClickPrevention();
    }, 100);
  },
});
</script>

<template>
  <div class="onboarding-tour">
    <!-- This component just handles the tour logic, no UI -->
  </div>
</template>

<style lang="scss" scoped>
.onboarding-tour {
  display: none;
}
</style>
